import { useCheckOverflow } from "@/hooks/useCheckOverflow";
import { TooltipPortal } from "@radix-ui/react-tooltip";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./index";
import { BasicTooltipProps } from "./types";

export enum OverflowDirection {
  HORIZONTAL = "horizontal",
  VERTICAL = "vertical",
}

export interface OverflowDisplayTooltipProps extends BasicTooltipProps {
  overflowDirection?: OverflowDirection;
  lineClamp?: number;
  styles?: React.CSSProperties;
}

export const OverflowDisplayTooltip = ({
  content,
  children,
  variant = "black",
  side = "top",
  usePortal = true,
  delayDuration,
  className,
  ignoreOverflow = false,
  overflowDirection = OverflowDirection.HORIZONTAL,
  lineClamp = 1,
  styles,
}: OverflowDisplayTooltipProps) => {
  const { ref, isOverflowing } = useCheckOverflow({});

  const renderContent = () => {
    return usePortal ? (
      <TooltipPortal>
        <TooltipContent side={side} variant={variant} style={{ zIndex: 9999 }}>
          {content}
        </TooltipContent>
      </TooltipPortal>
    ) : (
      <TooltipContent side={side} variant={variant} style={{ zIndex: 9999 }}>
        {content}
      </TooltipContent>
    );
  };

  const getOverflowClasses = () => {
    const baseClasses = "overflow-hidden";

    switch (overflowDirection) {
      case OverflowDirection.HORIZONTAL:
        return `${baseClasses} truncate`;
      case OverflowDirection.VERTICAL:
        return `${baseClasses} line-clamp-${lineClamp}`;
      default:
        return `${baseClasses} truncate`;
    }
  };

  return (
    <TooltipProvider delayDuration={delayDuration}>
      <Tooltip>
        <TooltipTrigger className={getOverflowClasses()} asChild>
          <span className={className} style={styles} ref={ref}>
            {children}
          </span>
        </TooltipTrigger>
        {(isOverflowing || ignoreOverflow) && renderContent()}
      </Tooltip>
    </TooltipProvider>
  );
};
